import './App.css';
import PhysicalLightScene from './components/PhysicalLightScene';
import { Link, Routes, Route } from 'react-router-dom';
import MagicalCursor from './components/MagicalCursor';
//framer motion
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, lazy, Suspense } from 'react';
import LoadingScreen from './components/LoadingScreen';
import Features from './components/Features';
import HowItWorks from './components/HowItWorks';
import Testimonials from './components/Testimonials';
import Demo from './components/Demo';
import FAQ from './components/FAQ';
import Pricing from './components/Pricing';
import PricingPage from './components/PricingPage';
import CheckoutPage from './components/CheckoutPage';
import SignInPage from './components/SignInPage';
import PaymentPage from './components/PaymentPage';
import Dashboard from './components/Dashboard';
import Footer from './components/Footer';
import Information from './components/Information';
import { ClerkProvider } from '@clerk/clerk-react';

// Component for suspense fallback
const ComponentLoader = () => (
  <div className="w-full h-32 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-yellow-700 rounded-full border-t-transparent animate-spin"></div>
  </div>
);

function App() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [isLoading, setIsLoading] = useState(true);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const [isCursorVisible, setIsCursorVisible] = useState(true);

  useEffect(() => {
    isDrawerOpen ? 
      document.body.style.cursor = 'default' : 
      document.body.style.cursor = 'none';
  }, [isDrawerOpen]);
  
  // Preload critical resources
  useEffect(() => {
    // Pre-cache important images and resources
    const preloadResources = async () => {
      try {
        // Create an array of promises for resources that need to be loaded
        const resourcePromises = [
          // Simulate resource loading for demonstration
          new Promise(resolve => setTimeout(resolve, 2000))
        ];
        
        // Wait for all resources to load
        await Promise.all(resourcePromises);
        setResourcesLoaded(true);
      } catch (error) {
        console.error("Error preloading resources:", error);
        // Still mark as loaded even if there's an error
        setResourcesLoaded(true);
      }
    };
    
    preloadResources();
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  // Show loading screen until resources are loaded and animation completes
  if (isLoading || !resourcesLoaded) {
    return <LoadingScreen onLoadingComplete={resourcesLoaded ? handleLoadingComplete : undefined} />;
  }

  return (
    <ClerkProvider publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || ''}>
      <motion.div 
        className="App" 
      style={{
        width: "100vw", 
        height: "100vh", 
        position: "fixed", 
        backgroundColor: "#000000", 
        overflowY: "auto"
      }}
      animate={{
        height: isDrawerOpen && !isDesktop ? "50vh" : "100vh",
        width: isDrawerOpen && isDesktop ? "50vw" : "100vw"
      }}
      transition={{ type: "spring", damping: 25, stiffness: 200 }}
    >
      {/* Logo in top left */}
      <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      >
      <Link to="/" className="absolute top-4 left-4 md:left-14 md:top-14 z-50 font-sans text-lg font-base text-yellow-700 flex items-center">
        <div className="font-serif text-[1rem] font-extrabold text-yellow-700 mr-2 w-6 h-6 border border-white flex items-center justify-center  shadow-md " style={{ boxShadow: '0 0 10px 2px rgba(255, 222, 35, 0.26)', borderColor: 'rgba(255, 255, 255, 0.7)' }}>W</div>
        WizardPortrait
      </Link>
      </motion.div>

      <Routes>
        <Route path="/info" element={<Suspense fallback={<ComponentLoader />}><Information /></Suspense>} />
        <Route path="/info/:section" element={<Suspense fallback={<ComponentLoader />}><Information /></Suspense>} />
        <Route path="/pricing" element={<Suspense fallback={<ComponentLoader />}><PricingPage /></Suspense>} />
        <Route path="/sign-in" element={<Suspense fallback={<ComponentLoader />}><SignInPage /></Suspense>} />
        <Route path="/checkout" element={<Suspense fallback={<ComponentLoader />}><CheckoutPage /></Suspense>} />
        <Route path="/payment" element={<Suspense fallback={<ComponentLoader />}><PaymentPage /></Suspense>} />
        <Route path="/dashboard" element={<Suspense fallback={<ComponentLoader />}><Dashboard /></Suspense>} />
        <Route path="/" element={
          <>
            {/* Main content - Physical Light Scene */}
            <PhysicalLightScene />
      
            {/* Scene information overlay */}
            <div className="font-serif absolute top-20 left-4 md:top-1/4 md:left-14 z-50 text-left  md:w-1/2 max-w-[300px]">
              {/* <h1 className="text-3xl font-bold text-white mb-4">Physical Light Demo</h1> */}
              <motion.p 
                className="text-yellow-800 text-base md:text-2xl mb-4 font-serif max-w-[300px] text-shadow-black "
                initial={{ opacity: 0, width: 0, overflow: "hidden", y: 0 }}
                style={{ textShadow: "2px 2px 4px rgba(0,0,0,1)" }}
                animate={{ 
                  opacity: [0, 1, 1, 0], 
                  width: "100%",
                  y: [0, -5, 0, -5, 0] 
                }}
                transition={{ 
                  opacity: { 
                    duration: 20, 
                    ease: "easeInOut",
                    times: [0, 0.25, 0.75, 1] 
                  },
                  width: { duration: 2, ease: [0.25, 1, 0.5, 1] },
                  y: { 
                    duration: 20, 
                    ease: "easeInOut"
                  }
                }}
              >
                {["Imagine", "the", "magic", "of", "a", "painting", "come", "alive,", 
                  "right", "on", "your", "TV", "screen,", "ready", "to", "listen", "and", "chat."].map((word, index) => (
                  <motion.span
                    key={index}
                    style={{ display: "inline-block", marginRight: "0.3rem" }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.5 + (index * 0.1) }}
                  >
                    {word}
                    {word === "alive," && <br/>}
                  </motion.span>
                ))}
              </motion.p>
            </div>

            <div className="font-serif absolute top-40 right-4 md:top-2/4 md:right-14 z-50 text-right  max-w-[300px]">
              <motion.p 
                className="text-yellow-800 text-base md:text-2xl mb-4 font-serif text-shadow-black"
                initial={{ opacity: 0, width: 0, overflow: "hidden", y: 0 }}
                style={{ textShadow: "2px 2px 4px rgba(0,0,0,1)" }}
                animate={{ 
                  opacity: [0, 1, 1, 0], 
                  width: "100%",
                  y: [0, -5, 0, -5, 0] 
                }}
                transition={{ 
                  opacity: { 
                    duration: 20, 
                    ease: "easeInOut",
                    times: [0, 0.25, 0.75, 1],
                    delay: 8 
                  },
                  width: { duration: 2, ease: [0.25, 1, 0.5, 1], delay: 8 },
                  y: { 
                    duration: 6, 
                    ease: "easeInOut",
                    delay: 8
                  }
                }}
              >
                {["Imagine", "a", "portrait", "so", "lifelike,", "it", "knows", "your", "name,", "shares", "your", "life,", "and", "tells", "jokes."].map((word, index) => (
                  <motion.span
                    key={index}
                    style={{ display: "inline-block", marginRight: "0.3rem", textShadow: "2px 2px 4px rgba(0,0,0,0.5)" }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 8.5 + (index * 0.1) }}
                  >
                    {word}
                    {word === "lifelike," && <br/>}
                  </motion.span>
                ))}
              </motion.p>
            </div>

            <div className="font-serif absolute bottom-32 left-4 md:bottom-1/4 md:left-14 z-50 text-left max-w-[300px]">
              <motion.p 
                className="text-yellow-700 text-base md:text-2xl mb-4 font-serif text-shadow-black "
                initial={{ opacity: 0, width: 0, overflow: "hidden", y: 0 }}
                style={{ textShadow: "2px 2px 4px rgba(0,0,0,1)" }}
                animate={{ 
                  opacity: [0, 1, 1, 0], 
                  width: "100%",
                  y: [0, -5, 0, -5, 0] 
                }}
                transition={{ 
                  opacity: { 
                    duration: 20, 
                    ease: "easeInOut",
                    times: [0, 0.25, 0.75, 1],
                    delay: 16 
                  },
                  width: { duration: 2, ease: [0.25, 1, 0.5, 1], delay: 16 },
                  y: { 
                    duration: 6, 
                    ease: "easeInOut",
                    delay: 16
                  }
                }}
              >
                {["Craft", "your", "enchanted", "companion—", "Choose", "their", "look,", "their", "voice,", "their", "story.", "Every", "portrait", "is", "uniquely", "yours."].map((word, index) => (
                  <motion.span
                    key={index}
                    style={{ display: "inline-block", marginRight: "0.3rem" }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 16.5 + (index * 0.1) }}
                  >
                    {word}
                    {word === "companion—" && <br/>}
                  </motion.span>
                ))}
              </motion.p>
            </div>

            <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            >
            <div className="hidden md:block absolute top-14 right-14 z-50 font-base text-yellow-700 text-[0.6rem] uppercase tracking-[2px] text-right w-64">
              Bring magic to life
            </div>
            </motion.div>

            <div className="absolute bottom-14 cursor-pointer z-50 font-base text-yellow-700 text-right pr-14 w-full" >
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 15 }}
              >
                <span className="scroll-indicator">
                  <div className="scroll-text">Scroll to explore</div>
                  <div className="scroll-icon">
                    <div className="scroll-dot"></div>
                  </div>
                </span>
              </motion.p>
            </div>

            <div>
              {/* add a video player here */}
              <motion.div>
                <div className="pt-2 h-full overflow-y-auto  mt-[100vh]">
                  <Suspense fallback={<ComponentLoader />}>
                    <Demo />
                    <Features />
                    <HowItWorks />
                    <Testimonials />
                    <Pricing />
                    <FAQ />
                    <Footer />
                  </Suspense>
                  {/* <Demo /> */}
                </div>
              </motion.div>
            </div>
          </>
        } />
      </Routes>

      {!isDrawerOpen && <MagicalCursor />}
    </motion.div>
    </ClerkProvider>
  );
}

export default App;
